import { useState } from 'preact/hooks';
import { getConfig } from '../config';
import { deleteAccount } from '../api';
import { store } from '../store';
import { navigateToView } from '../navigation';
import { t } from '../i18n';

export function DeleteAccountView() {
  const config = getConfig();
  const [passwd, setPasswd] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});
    const p = passwd.trim();
    const r = reason.trim();
    if (!p) {
      setFieldErrors((prev) => ({ ...prev, passwd: t('deleteAccount.passwordRequired') }));
      return;
    }
    if (!r) {
      setFieldErrors((prev) => ({ ...prev, reason: t('deleteAccount.reasonRequired') }));
      return;
    }
    setLoading(true);
    const res = await deleteAccount(config, p, r);
    setLoading(false);
    if (res.status === 200 && res.result !== undefined) {
      store.setAuth(null);
      navigateToView(null);
      store.addToast(t('deleteAccount.success'), 'success');
      return;
    }
    const errs = res.errors as Record<string, string[]> | undefined;
    if (errs) {
      const next: Record<string, string> = {};
      if (errs.passwd?.length) next.passwd = errs.passwd[0];
      if (errs.reason?.length) next.reason = errs.reason[0];
      setFieldErrors(next);
    }
    setError(res.error ?? t('deleteAccount.failed'));
  };

  return (
    <div class="sw-tickets-shell delete-account-shell">
      <div class="delete-account-view sw-surface-card">
        <div class="delete-account-view__header">
          <p class="delete-account-view__intro mb-2">{t('deleteAccount.thanks')}</p>
          <p class="delete-account-view__intro mb-0">{t('deleteAccount.safetyInfo')}</p>
        </div>
        <div class="delete-account-view__rules-block">
          <p class="delete-account-view__rules-title mb-2">{t('deleteAccount.rules')}</p>
          <ol class="delete-account-view__rules mb-0">
            <li>{t('deleteAccount.rule1')}</li>
            <li>{t('deleteAccount.rule2')}</li>
          </ol>
        </div>
        <div class="alert alert-warning small delete-account-view__warning" role="alert">
          <strong>{t('deleteAccount.attention')}</strong> {t('deleteAccount.warning')}
        </div>
        <form onSubmit={handleSubmit}>
          <div class="mb-3">
            <label class="form-label" for="delete-passwd">{t('deleteAccount.password')}</label>
            <input
              type="password"
              class={`form-control ${fieldErrors.passwd ? 'is-invalid' : ''}`}
              id="delete-passwd"
              autocomplete="current-password"
              value={passwd}
              onInput={(e) => setPasswd((e.target as HTMLInputElement).value)}
              disabled={loading}
            />
            {fieldErrors.passwd && <div class="text-danger small mt-1">{fieldErrors.passwd}</div>}
          </div>
          <div class="mb-3">
            <label class="form-label" for="delete-reason">{t('deleteAccount.reason')}</label>
            <textarea
              class={`form-control ${fieldErrors.reason ? 'is-invalid' : ''}`}
              id="delete-reason"
              rows={3}
              placeholder={t('deleteAccount.reasonHint')}
              value={reason}
              onInput={(e) => setReason((e.target as HTMLTextAreaElement).value)}
              disabled={loading}
            />
            {fieldErrors.reason && <div class="text-danger small mt-1">{fieldErrors.reason}</div>}
          </div>
          {error && <div class="alert alert-danger py-2 mb-3 small">{error}</div>}
          <div class="text-center">
            <button type="submit" class="btn btn-danger" disabled={loading}>
              {loading ? <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span> : null}
              {t('deleteAccount.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
